import { Injectable } from "@angular/core";
import { BaseApiService } from "../base-api.service";
import { HttpClient } from "@angular/common/http";
import { delay, firstValueFrom, map, Observable } from "rxjs";
import { environment } from "@beyou/environments/environment";
import { TherapeuticDomain, TherapeuticDomainBase, TherapeuticDomainTranslation } from "../../models/therapeutic-domain.model";
import { TherapeuticDomainMapper } from "../../mappers/therapeutic-domain.mapper";
import { TherapeuticDomainDto } from "../../dto/therapeutic-domains/therapeutic-domain.dto";
import { TherapeuticDomainTranslationsDto } from "../../dto/therapeutic-domains/therapeutic-domain-translations.dto";
import { TherapeuticDomainTranslationMapper } from "../../mappers/therapeutic-domain-translation.mapper";
import { ExportResultDto } from "../../dto/export-result.dto";

@Injectable({
  providedIn: 'root'
})
export class TherapeuticDomainApiService extends BaseApiService {

  constructor(http: HttpClient) {
    super(http);
  }

  public getAll(): Observable<TherapeuticDomain[]> {
    const apiUrl = `${environment.adminApiBaseUrl}/domains`;
    return this.getAllPagedData<TherapeuticDomainDto>(apiUrl).pipe(
      map((response) => {
        return response.map(item => {
          return TherapeuticDomainMapper.toModel(item);
        })
      })
    );
  }

  public async add(therapeuticDomain: TherapeuticDomainBase): Promise<TherapeuticDomain> {
    const apiUrl = `${environment.adminApiBaseUrl}/domains`;
    const dto = TherapeuticDomainMapper.toCreateDto(therapeuticDomain);
    console.debug('new therapeutic domain dto', dto);
    return await firstValueFrom(this.http.post<TherapeuticDomainDto>(apiUrl, dto).pipe(
      delay(3000), // Allow time for the cover image to be resized on S3
      map((response) => {
        console.debug('therapeutic domain added: ', response.id);
        return TherapeuticDomainMapper.toModel(response);
      })
    ));
  }

  public async update(therapeuticDomain: TherapeuticDomain): Promise<TherapeuticDomain> {
    const apiUrl = `${environment.adminApiBaseUrl}/domains/${therapeuticDomain.id}`;
    const dto = TherapeuticDomainMapper.toUpdateDto(therapeuticDomain);
    return await firstValueFrom(this.http.put<TherapeuticDomainDto>(apiUrl, dto).pipe(
      delay(3000), // Allow time for the cover image to be resized on S3
      map((response) => {
        console.debug('therapeutic domain updated: ', response.id);
        return TherapeuticDomainMapper.toModel(response);
      })
    ));
  }

  public async delete(id: number): Promise<void> {
    const apiUrl = `${environment.adminApiBaseUrl}/domains/${id}`;
    try {
      await firstValueFrom(this.http.delete(apiUrl));
      console.log('Therapeutic domain deleted: ', id);
    } catch (error) {
      console.error('Error deleting therapeutic domain with id: ', id, error);
      throw error; // Re-throw the error to handle it in the calling code
    }
  }

  public async getTranslations(id: number): Promise<TherapeuticDomainTranslation[]> {
    const apiUrl = `${environment.adminApiBaseUrl}/domains/${id}/translations`;
    return await firstValueFrom(this.http.get<TherapeuticDomainTranslationsDto>(apiUrl).pipe(
      map((response) => {
        console.debug('therapeutic domain translations retrieved: ', id);
        return response.translations.map(item => TherapeuticDomainTranslationMapper.toModel(item));
      })
    ));
  }

  public async updateTranslations(id: number, translations: TherapeuticDomainTranslation[]): Promise<void> {
    const apiUrl = `${environment.adminApiBaseUrl}/domains/${id}/translations`;
    const dto = {
      translations: translations.map(item => TherapeuticDomainTranslationMapper.toDto(item))
    };
    await firstValueFrom(this.http.post(apiUrl, dto));
    console.debug('therapeutic domain translations updated: ', id);
  }

  public async export(languageId: number): Promise<ExportResultDto> {
    const apiUrl = `${environment.adminApiBaseUrl}/domains/export?language_id=${languageId}`;
    return await firstValueFrom(this.http.get<ExportResultDto>(apiUrl));
  }

  public async import(file: File): Promise<void> {
    const uploadData = new FormData();
    uploadData.append('file', file);
    const apiUrl = `${environment.adminApiBaseUrl}/domains/import`;
    await firstValueFrom(this.http.post(apiUrl, uploadData));
  }

}